/**
 * Phase 06.2 / DEC-033 — platform-managed site_types catalog.
 *
 * Platform-managed: NO tenant_id, NO pgPolicy. Publicly readable across
 * tenants. Writes reserved for PLATFORM_ADMIN role via Phase 9 admin endpoint
 * (uses platformDb client to bypass app_user GRANT restrictions).
 *
 * Same posture as `disposal_facilities` (disposals.ts:201-247).
 *
 * 6-row platform seed (Plan 06.2-08):
 *   WELL (sort_order 10), RIG (20), TANK_BATTERY (30), DISPOSAL (40),
 *   PIPELINE (50), OTHER (999).
 */

import { pgTable, uuid, text, integer, boolean, timestamp, uniqueIndex } from 'drizzle-orm/pg-core';

export const siteTypes = pgTable(
  'site_types',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    code: text('code').notNull(),
    label: text('label').notNull(),
    isActive: boolean('is_active').notNull().default(true),
    sortOrder: integer('sort_order').notNull().default(0),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex('site_types_code_idx').on(t.code),
    // No pgPolicy — DEC-033 #5 platform-managed catalog.
    //
    // CORRECTED 2026-09-28 (86bc7raat). This comment previously claimed 'write
    // access REVOKEd from app_user (only PLATFORM_ADMIN via platformDb client
    // mutates)'. Both halves were untrue and the claim cost real time: it is why
    // migration 0089 was written to 'restore' a write path that was never blocked.
    // 0089 therefore grants nothing and is a NO-OP — but it HAS been applied, so its
    // file must never be edited again, not even its comments: the migrate guard matches
    // applied migrations by sha256 of the file contents, and changing a byte makes it
    // report the migration as skipped and fails every deploy. That is what happened on
    // 2026-09-28 when the correction below was first written into 0089's header instead
    // of here.
    //
    // What is actually true: 0000_init.sql's epilogue does
    //   ALTER DEFAULT PRIVILEGES IN SCHEMA public
    //     GRANT SELECT, INSERT, UPDATE, DELETE ON TABLES TO app_user
    // and REVOKEs write on only four named tables (job_statuses, job_types,
    // disposal_facilities, and paelode_tenant_config until 0203 dropped it). site_types is on NO revoke
    // list, so app_user has held all four privileges since 0019 created it —
    // 0019's explicit GRANT SELECT was redundant, not restrictive. And
    // request.platformDb is a TS-narrowed VIEW over the same app_user
    // connection, so it confers no privilege the ordinary client lacks.
    //
    // The guard on a write here is therefore the API permission check
    // (MANAGE_SITE_TYPES, PLATFORM_ADMIN only) and nothing else. With no RLS on
    // this table, that is the entire enforcement surface — worth knowing before
    // adding another writer.
  ],
);

export type SiteType = typeof siteTypes.$inferSelect;
export type NewSiteType = typeof siteTypes.$inferInsert;
